import { useCallback } from 'react'

import { EyeOff, X } from 'lucide-react'
import type { FC } from 'react'

import { Button, buttonVariants } from '@/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

export interface HideColumnFilterOptions {
	label: string
	value: string
}

interface Props {
	onChange: (newValues: Record<string, boolean>) => void
	value: Record<string, boolean>
	options: HideColumnFilterOptions[]
}

const HideColumnFilter: FC<Props> = ({ onChange, options, value }) => {
	const filteredColumns = Object.values(value).filter(val => !val)

	const resetFilters = useCallback(
		(e: { stopPropagation: () => void }) => {
			e.stopPropagation()
			onChange(
				options.reduce<Record<string, boolean>>((prev, curr) => {
					prev[curr.value] = true
					return prev
				}, {})
			)
		},
		[onChange, options]
	)

	return (
		<DropdownMenu>
			<div
				className={cn(
					buttonVariants({ variant: 'ghost', size: 'sm' }),
					filteredColumns.length > 0
						? 'pr-0 bg-accent dark:bg-accent/50 hover:bg-accent/50'
						: ''
				)}
			>
				<DropdownMenuTrigger>
					<div className="flex items-center gap-2">
						<EyeOff />
						<p>
							{filteredColumns.length > 0
								? `${filteredColumns.length} hidden columns`
								: 'Hide columns'}
						</p>
					</div>
				</DropdownMenuTrigger>
				{filteredColumns.length > 0 && (
					<Button
						aria-hidden="false"
						aria-label="remove applied filters"
						onClick={resetFilters}
						variant="ghost"
						size="sm"
					>
						<X />
					</Button>
				)}
			</div>
			<DropdownMenuContent>
				{options.map(option => (
					<DropdownMenuCheckboxItem
						key={`asta-hide-column-option-${option.value}`}
						checked={!value[option.value]}
						onCheckedChange={() =>
							onChange({
								...value,
								[option.value]: !value[option.value],
							})
						}
					>
						{option.label}
					</DropdownMenuCheckboxItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}

export default HideColumnFilter
